import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Responsive, Layout, Layouts } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { useGetModel } from '../../../../../api/routes/models'
import { useGetModelAttributes } from '../../../../../api/routes/model-attributes'
import {
    useGetDashboard,
    useUpdateDashboard,
    DashboardWidget,
} from '../../../../../api/routes/dashboards'
import {
    ArrowLeft,
    Gauge,
    LineChart,
    Map as MapIcon,
    AlertCircle,
    Trash2,
    Save,
    X,
    Check,
    ChevronsUpDown,
    GripVertical,
    ChevronUp,
    ChevronDown,
    Monitor,
    Laptop,
    Tablet,
    Smartphone,
    Heading,
    Activity,
    Battery,
    BatteryFull,
    Cpu,
    Navigation,
    Route,
    Settings,
    Timer,
    Thermometer,
    Weight,
    WeightIcon,
    Zap,
    MapPin,
    Wifi,
    WifiOff,
    MoreVertical,
    TrendingUp,
    LandPlot,
    ArrowLeftRight,
    RefreshCw,
    ChartNoAxesCombined,
    LayoutGrid,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { Button } from '../../../../../components/ui/button'
import { Input } from '../../../../../components/ui/input'
import { Label } from '../../../../../components/ui/label'
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from '../../../../../components/ui/select'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../../../../components/ui/popover'
import { cn } from '../../../../../lib/utils'

const BREAKPOINTS = { lg: 996, sm: 0 }
const COLS = { lg: 12, sm: 6 }
type Breakpoint = keyof typeof COLS
const BP_ORDER: Breakpoint[] = ['lg', 'sm']
const ROW_HEIGHT = 60

type WidgetType = DashboardWidget['type']
type PreviewMode = 'auto' | 'desktop' | 'laptop' | 'tablet' | 'phone'

type ModelAttribute = {
    id: string
    name: string
    friendlyName?: string | null
    dataType?: string
    unit?: string | null
}

type AttributeComboboxProps = {
    attributes: ModelAttribute[]
    value?: string | null
    id?: string
    placeholder?: string
    emptyValue?: string
    emptyLabel?: string
    onChange: (value: string) => void
}

type ValueGroupItem = {
    attributeKey?: string | null
    label?: string
    unit?: string | null
}

const VALUE_GROUP_DEFAULT_COLUMNS = 2
const VALUE_GROUP_DEFAULT_ROWS = 3
const VALUE_GROUP_MIN_COLUMNS = 1
const VALUE_GROUP_MAX_COLUMNS = 6
const VALUE_GROUP_MIN_ROWS = 1
const VALUE_GROUP_MAX_ROWS = 10
const VALUE_GROUP_NONE = '__none__'

const WIDGET_DEFS: Record<
    WidgetType,
    {
        label: string
        icon: typeof Gauge
        size: { w: number; h: number; minW: number; minH: number }
        bindable: boolean
        fullWidth?: boolean
    }
> = {
    metric: { label: 'Metric Card', icon: Gauge, size: { w: 3, h: 2, minW: 2, minH: 2 }, bindable: true },
    chart: { label: 'Live Chart', icon: LineChart, size: { w: 5, h: 4, minW: 3, minH: 3 }, bindable: true },
    map: { label: 'Map', icon: MapIcon, size: { w: 4, h: 5, minW: 3, minH: 4 }, bindable: false },
    status: { label: 'Status / Errors', icon: AlertCircle, size: { w: 4, h: 4, minW: 2, minH: 3 }, bindable: false },
    header: { label: 'Section Header', icon: Heading, size: { w: 12, h: 1, minW: 2, minH: 1 }, bindable: false, fullWidth: true },
    valueGroup: { label: 'Value Group', icon: LayoutGrid, size: { w: 6, h: 4, minW: 1, minH: 2 }, bindable: false },
}

type IconOption = {
    value: string
    label: string
    icon: typeof Gauge | null
}

const ICON_OPTIONS: IconOption[] = [
    { value: 'default', label: 'Default', icon: null },
    { value: 'none', label: 'No icon', icon: null },
    { value: 'activity', label: 'Activity', icon: Activity },
    { value: 'alert-circle', label: 'Alert', icon: AlertCircle },
    { value: 'battery', label: 'Battery', icon: Battery },
    { value: 'battery-full', label: 'Battery Full', icon: BatteryFull },
    { value: 'chart-no-axes-combined', label: 'Timeline', icon: ChartNoAxesCombined },
    { value: 'chevron-down', label: 'Chevron Down', icon: ChevronDown },
    { value: 'chevron-up', label: 'Chevron Up', icon: ChevronUp },
    { value: 'cpu', label: 'CPU', icon: Cpu },
    { value: 'gauge', label: 'Gauge', icon: Gauge },
    { value: 'land-plot', label: 'Land Plot', icon: LandPlot },
    { value: 'layout-grid', label: 'Value Grid', icon: LayoutGrid },
    { value: 'line-chart', label: 'Chart', icon: LineChart },
    { value: 'map', label: 'Map', icon: MapIcon },
    { value: 'map-pin', label: 'Location', icon: MapPin },
    { value: 'more-vertical', label: 'More', icon: MoreVertical },
    { value: 'navigation', label: 'Navigation', icon: Navigation },
    { value: 'refresh-cw', label: 'Swap Circle', icon: RefreshCw },
    { value: 'route', label: 'Route', icon: Route },
    { value: 'settings', label: 'Settings', icon: Settings },
    { value: 'settings-suggest', label: 'Settings Suggest', icon: Settings },
    { value: 'swap-horizontal', label: 'Swap Horizontal', icon: ArrowLeftRight },
    { value: 'thermometer', label: 'Temperature', icon: Thermometer },
    { value: 'timer', label: 'Timer', icon: Timer },
    { value: 'trending-up', label: 'Trending Up', icon: TrendingUp },
    { value: 'weight', label: 'Weight', icon: Weight },
    { value: 'weight-icon', label: 'Weight Icon', icon: WeightIcon },
    { value: 'wifi', label: 'Connection', icon: Wifi },
    { value: 'wifi-off', label: 'Disconnected', icon: WifiOff },
    { value: 'zap', label: 'Power', icon: Zap },
]

const iconOptionForWidget = (widget: DashboardWidget) => {
    const iconKey =
        typeof widget.options?.icon === 'string'
            ? widget.options.icon
            : 'default'
    return (
        ICON_OPTIONS.find((option) => option.value === iconKey) ??
        ICON_OPTIONS[0]
    )
}

const iconForWidget = (widget: DashboardWidget) => {
    const option = iconOptionForWidget(widget)
    if (option.value === 'none') return null
    return option.icon ?? WIDGET_DEFS[widget.type].icon
}

const iconLabelForWidget = (widget: DashboardWidget) => {
    const option = iconOptionForWidget(widget)
    if (option.value === 'none') return 'No icon'
    return option.value === 'default'
        ? `Default (${WIDGET_DEFS[widget.type].label})`
        : option.label
}

const titleOverride = (title?: string | null) =>
    typeof title === 'string' ? title.trim() : ''

const titleLabelForWidget = (widgetType: WidgetType) =>
    widgetType === 'metric' ? 'Title override' : 'Title'

const titlePlaceholderForWidget = (widgetType: WidgetType) => {
    if (widgetType === 'metric') return 'Use attribute friendly name'
    if (widgetType === 'valueGroup') return 'Optional group title'
    return undefined
}

const PREVIEW_MODES: {
    key: PreviewMode
    label: string
    icon: typeof Monitor
    width: number | null
    layout: Breakpoint | null
}[] = [
    { key: 'auto', label: 'Auto', icon: Monitor, width: null, layout: null },
    { key: 'desktop', label: 'Desktop', icon: Monitor, width: 1280, layout: 'lg' },
    { key: 'laptop', label: 'Laptop', icon: Laptop, width: 1100, layout: 'lg' },
    { key: 'tablet', label: 'Tablet', icon: Tablet, width: 800, layout: 'sm' },
    { key: 'phone', label: 'Phone', icon: Smartphone, width: 520, layout: 'sm' },
]

let widgetCounter = 0
const nextWidgetId = () => `w${Date.now()}_${widgetCounter++}`

const numberOr = (value: unknown, fallback: number) => {
    const next = Number(value)
    return Number.isFinite(next) ? next : fallback
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

const clampInteger = (value: unknown, fallback: number, min: number, max: number) =>
    clamp(Math.round(numberOr(value, fallback)), min, max)

const getValueGroupColumns = (widget: DashboardWidget) =>
    clampInteger(
        widget.options?.columns,
        VALUE_GROUP_DEFAULT_COLUMNS,
        VALUE_GROUP_MIN_COLUMNS,
        VALUE_GROUP_MAX_COLUMNS
    )

const getValueGroupRows = (widget: DashboardWidget) =>
    clampInteger(
        widget.options?.rows,
        VALUE_GROUP_DEFAULT_ROWS,
        VALUE_GROUP_MIN_ROWS,
        VALUE_GROUP_MAX_ROWS
    )

const normalizeValueGroupItem = (item: unknown): ValueGroupItem => {
    if (!item || typeof item !== 'object') return {}
    const value = item as Record<string, unknown>
    return {
        attributeKey:
            typeof value.attributeKey === 'string' ? value.attributeKey : null,
        label: typeof value.label === 'string' ? value.label : undefined,
        unit: typeof value.unit === 'string' ? value.unit : null,
    }
}

const getValueGroupItems = (widget: DashboardWidget) => {
    const values = widget.options?.values
    if (!Array.isArray(values)) return []
    return values.map(normalizeValueGroupItem)
}

const serializeValueGroupItems = (values: ValueGroupItem[]) =>
    values.map((item) => {
        if (!item.attributeKey) return {}
        const label = titleOverride(item.label)
        return {
            attributeKey: item.attributeKey,
            ...(label ? { label } : {}),
        }
    })

const normalizeLayoutItem = (layout: Partial<Layout>, widget: DashboardWidget, bp: Breakpoint): Layout => {
    const widgetDef = WIDGET_DEFS[widget.type]
    const def = widgetDef.size
    const cols = COLS[bp]
    const minW = Math.min(def.minW, cols)
    const fallbackW = widgetDef.fullWidth ? cols : def.w
    const w = clamp(numberOr(layout.w, fallbackW), minW, cols)
    const h = Math.max(numberOr(layout.h, def.h), def.minH)
    const x = clamp(numberOr(layout.x, 0), 0, Math.max(0, cols - w))
    const y = Math.max(0, numberOr(layout.y, 0))

    return {
        i: widget.i,
        x,
        y,
        w,
        h,
        minW,
        minH: def.minH,
    }
}

const storedLayoutForBreakpoint = (widget: DashboardWidget, bp: Breakpoint) => {
    if (bp === 'lg') return widget.layouts?.lg ?? widget.layouts?.md
    return widget.layouts?.sm ?? widget.layouts?.xs ?? widget.layouts?.xxs ?? widget.layouts?.lg
}

// Build RGL `layouts` (per-breakpoint arrays) from stored widgets.
const widgetsToLayouts = (widgets: DashboardWidget[]): Layouts => {
    const layouts: Layouts = {}
    for (const bp of BP_ORDER) {
        layouts[bp] = widgets.map((widget) => {
            const stored = storedLayoutForBreakpoint(widget, bp)
            return normalizeLayoutItem(stored ?? {}, widget, bp)
        })
    }
    return layouts
}

const normalizeLayouts = (
    nextLayouts: Layouts,
    meta: Record<string, DashboardWidget>,
    fallback: Layouts = {}
): Layouts => {
    const layouts: Layouts = {}
    for (const bp of BP_ORDER) {
        layouts[bp] = (nextLayouts[bp] ?? fallback[bp] ?? [])
            .filter((layout) => Boolean(meta[layout.i]))
            .map((layout) => normalizeLayoutItem(layout, meta[layout.i], bp))
    }
    return layouts
}

const serializeLayouts = (layouts: Layouts) =>
    JSON.stringify(
        BP_ORDER.map((bp) => [
            bp,
            (layouts[bp] ?? [])
                .map((layout) => ({
                    i: layout.i,
                    x: layout.x,
                    y: layout.y,
                    w: layout.w,
                    h: layout.h,
                }))
                .sort((a, b) => a.i.localeCompare(b.i)),
        ])
    )

const isWidgetControlTarget = (target: EventTarget | null) =>
    target instanceof Element &&
    Boolean(
        target.closest(
            ".widget-no-drag, button, input, textarea, [role='combobox'], [role='option']"
        )
    )

const AttributeCombobox = ({
    attributes,
    value,
    id,
    placeholder = 'Select an attribute',
    emptyValue,
    emptyLabel = 'Empty',
    onChange,
}: AttributeComboboxProps) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const selected = attributes.find((attr) => attr.name === value)
    const selectedLabel = selected?.friendlyName || selected?.name
    const normalizedQuery = query.trim().toLowerCase()
    const filteredAttributes = normalizedQuery
        ? attributes.filter((attr) =>
              [
                  attr.friendlyName,
                  attr.name,
                  attr.unit,
                  attr.dataType,
              ]
                  .filter(Boolean)
                  .join(' ')
                  .toLowerCase()
                  .includes(normalizedQuery)
          )
        : attributes

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen)
                if (!nextOpen) setQuery('')
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    id={id}
                    role="combobox"
                    aria-expanded={open}
                    aria-label={placeholder}
                    className="widget-no-drag w-full justify-between"
                >
                    <span className="truncate">
                        {selectedLabel || placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="widget-no-drag w-[var(--radix-popover-trigger-width)] p-0"
            >
                <div className="border-b p-2">
                    <Input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search attributes..."
                        className="h-8"
                    />
                </div>
                <div className="max-h-64 overflow-y-auto p-1">
                    {emptyValue && (
                        <button
                            type="button"
                            role="option"
                            aria-selected={value === emptyValue || !value}
                            className={cn(
                                'flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                                (value === emptyValue || !value) &&
                                    'bg-accent text-accent-foreground'
                            )}
                            onClick={() => {
                                onChange(emptyValue)
                                setOpen(false)
                                setQuery('')
                            }}
                        >
                            <Check
                                className={cn(
                                    'h-4 w-4 shrink-0',
                                    value === emptyValue || !value
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                )}
                            />
                            <span className="min-w-0 flex-1 truncate">
                                {emptyLabel}
                            </span>
                        </button>
                    )}
                    {filteredAttributes.length === 0 ? (
                        <p className="px-2 py-3 text-sm text-muted-foreground">
                            No attributes found.
                        </p>
                    ) : (
                        filteredAttributes.map((attr) => {
                            const label = attr.friendlyName || attr.name
                            const active = attr.name === value
                            return (
                                <button
                                    key={attr.id}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    className={cn(
                                        'flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                                        active &&
                                            'bg-accent text-accent-foreground'
                                    )}
                                    onClick={() => {
                                        onChange(attr.name)
                                        setOpen(false)
                                        setQuery('')
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            'h-4 w-4 shrink-0',
                                            active
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate">
                                            {label}
                                        </span>
                                        {attr.unit && (
                                            <span className="block truncate text-xs text-muted-foreground">
                                                {attr.unit}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            )
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}

const WidgetPreview = ({
    widget,
    label,
    attributeLabelForKey,
    attributeUnitForKey,
}: {
    widget: DashboardWidget
    label: string | null
    attributeLabelForKey: (key?: string | null) => string | null
    attributeUnitForKey: (key?: string | null) => string
}) => {
    const unit = attributeUnitForKey(widget.attributeKey)
    const overrideTitle = titleOverride(widget.title)
    const displayLabel =
        widget.type === 'metric'
            ? overrideTitle || label || 'Select an attribute'
            : label || overrideTitle || WIDGET_DEFS[widget.type].label
    const PreviewIcon = iconForWidget(widget)

    if (widget.type === 'header') {
        const subtitle =
            typeof widget.options?.subtitle === 'string'
                ? widget.options.subtitle
                : ''

        return (
            <div className="flex h-full w-full items-center text-left">
                <div className="min-w-0">
                    <h2 className="truncate text-sm font-medium uppercase tracking-wider text-muted-foreground">
                        {widget.title || 'Section Header'}
                    </h2>
                    {subtitle && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        )
    }

    if (widget.type === 'metric') {
        return (
            <div className="h-full w-full rounded-lg border bg-card p-3 text-left sm:p-4">
                <div className="flex h-full items-start justify-between">
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {displayLabel}
                        </p>
                        <p className="mt-1 text-lg text-foreground sm:text-2xl">
                            --
                            {unit && (
                                <span className="ml-1 text-sm text-muted-foreground sm:text-lg">
                                    {unit}
                                </span>
                            )}
                        </p>
                    </div>
                    {PreviewIcon && (
                        <PreviewIcon className="ml-2 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    )}
                </div>
            </div>
        )
    }

    if (widget.type === 'chart') {
        return (
            <div className="flex h-full w-full flex-col gap-3 rounded-lg border bg-card p-4 text-left sm:p-5">
                <div className="flex items-start justify-between gap-3">
                    <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {displayLabel}
                    </p>
                    {PreviewIcon && (
                        <PreviewIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    )}
                </div>
                <div className="relative min-h-0 flex-1 rounded-md border bg-background/80">
                    <div className="absolute inset-x-3 bottom-3 top-3 flex items-end gap-1.5">
                        {[35, 52, 44, 72, 58, 80, 66].map((height, index) => (
                            <div
                                key={index}
                                className="flex-1 rounded-sm bg-primary/50"
                                style={{ height: `${height}%` }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    if (widget.type === 'valueGroup') {
        const columns = getValueGroupColumns(widget)
        const rows = getValueGroupRows(widget)
        const items = getValueGroupItems(widget)
        const itemCount = columns * rows
        const groupTitle =
            typeof widget.title === 'string' ? widget.title.trim() : ''

        return (
            <div className="flex h-full w-full flex-col rounded-lg border bg-card p-2 text-left sm:p-3">
                {groupTitle && (
                    <div className="mb-3 flex items-start justify-between gap-2 sm:mb-4">
                        <h3 className="min-w-0 truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {groupTitle}
                        </h3>
                        {PreviewIcon && (
                            <PreviewIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        )}
                    </div>
                )}
                <div
                    className="grid min-h-0 flex-1 content-start overflow-hidden gap-x-3 gap-y-2 sm:gap-x-5 sm:gap-y-3"
                    style={{
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    }}
                >
                    {Array.from({ length: itemCount }).map((_, index) => {
                        const item = items[index]
                        if (!item?.attributeKey) {
                            return <div key={index} className="min-w-0" />
                        }

                        const itemLabel =
                            titleOverride(item?.label) ||
                            attributeLabelForKey(item?.attributeKey) ||
                            ''
                        const unit = attributeUnitForKey(item?.attributeKey)

                        return (
                            <div key={index} className="min-w-0">
                                <p className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                    {itemLabel}
                                </p>
                                <p className="mt-0.5 truncate text-sm font-medium text-foreground sm:text-base">
                                    --
                                    {unit && (
                                        <span className="ml-1 text-xs text-muted-foreground">
                                            {unit}
                                        </span>
                                    )}
                                </p>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    if (widget.type === 'map') {
        return (
            <div className="relative h-full w-full overflow-hidden rounded-lg border bg-card">
                <div className="absolute inset-0 bg-[linear-gradient(90deg,hsl(var(--border))_1px,transparent_1px),linear-gradient(0deg,hsl(var(--border))_1px,transparent_1px)] bg-[size:28px_28px] opacity-50" />
                <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-md bg-white/90 px-2 py-1 text-xs font-medium text-muted-foreground shadow-sm">
                    {PreviewIcon && <PreviewIcon className="h-3.5 w-3.5" />}
                    Live Map
                </div>
                <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary shadow" />
            </div>
        )
    }

    return (
        <div className="flex h-full w-full flex-col gap-3 rounded-lg border bg-card p-4 text-left sm:p-5">
            <div className="flex items-start justify-between gap-3">
                <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {displayLabel}
                </p>
                {PreviewIcon && (
                    <PreviewIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                )}
            </div>
            <div className="grid min-h-0 flex-1 content-center gap-2">
                {['Drive system', 'Power module', 'Telemetry'].map((item, index) => (
                    <div
                        key={item}
                        className="flex items-center justify-between gap-2"
                    >
                        <span className="truncate text-xs text-foreground">{item}</span>
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                index === 1
                                    ? 'border border-red-200 bg-red-50 text-red-700'
                                    : 'border border-green-200 bg-green-50 text-green-700'
                            }`}
                        >
                            {index === 1 ? 'FAULT' : 'ONLINE'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export const DashboardConfiguratorRoute = () => {
    const { id: modelId, dashboardId } = useParams<{ id: string; dashboardId: string }>()

    const { data: model } = useGetModel(modelId!)
    const { data: dashboard, isLoading } = useGetDashboard(dashboardId)
    const { data: attributesRaw } = useGetModelAttributes(modelId!)
    const attributes: ModelAttribute[] = attributesRaw ?? []

    const updateDashboard = useUpdateDashboard()

    // Widget metadata keyed by widget id; layout positions held in RGL `layouts`.
    const [meta, setMeta] = useState<Record<string, DashboardWidget>>({})
    const [layouts, setLayouts] = useState<Layouts>({})
    const [breakpoint, setBreakpoint] = useState<Breakpoint>('lg')
    const [previewMode, setPreviewMode] = useState<PreviewMode>('desktop')
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [dirty, setDirty] = useState(false)
    const metaRef = useRef<Record<string, DashboardWidget>>({})
    const layoutsRef = useRef<Layouts>({})

    // Measure the canvas width ourselves instead of using WidthProvider, which
    // renders at 0 width under React 18 StrictMode (making items ungrabbable)
    // and only reacts to window — not container — resizes. A callback ref runs
    // whenever the canvas node mounts, so it works regardless of when the
    // dashboard finishes loading (a plain mount effect would miss it).
    const [gridWidth, setGridWidth] = useState(0)
    const resizeObserverRef = useRef<ResizeObserver | null>(null)
    const setCanvasRef = useCallback((el: HTMLDivElement | null) => {
        resizeObserverRef.current?.disconnect()
        if (!el) return
        setGridWidth(el.clientWidth)
        const ro = new ResizeObserver(() => setGridWidth(el.clientWidth))
        ro.observe(el)
        resizeObserverRef.current = ro
    }, [])

    // Hydrate from the loaded dashboard once.
    useEffect(() => {
        if (!dashboard) return
        const metaMap: Record<string, DashboardWidget> = {}
        for (const widget of dashboard.widgets ?? []) metaMap[widget.i] = widget
        const nextLayouts = widgetsToLayouts(dashboard.widgets ?? [])
        metaRef.current = metaMap
        layoutsRef.current = nextLayouts
        setMeta(metaMap)
        setLayouts(nextLayouts)
        setDirty(false)
    }, [dashboard])

    const orderedWidgets = useMemo(() => Object.values(meta), [meta])

    const attributeLabel = (key?: string | null) => {
        if (!key) return null
        const attr = attributes.find((a) => a.name === key)
        return attr?.friendlyName || attr?.name || key
    }

    const attributeUnit = (key?: string | null) => {
        if (!key) return ''
        const attr = attributes.find((a) => a.name === key)
        return attr?.unit || ''
    }

    const selectWidget = (id: string) => {
        setSelectedId(id)
    }

    const handlePreviewModeChange = (mode: PreviewMode) => {
        const preview = PREVIEW_MODES.find((entry) => entry.key === mode)
        setPreviewMode(mode)
        if (preview?.layout) setBreakpoint(preview.layout)
    }

    const addWidget = (type: WidgetType) => {
        const id = nextWidgetId()
        const def = WIDGET_DEFS[type]
        const widget: DashboardWidget = {
            i: id,
            type,
            layouts: {},
            attributeKey: null,
            title: type === 'metric' || type === 'valueGroup' ? null : def.label,
            options:
                type === 'valueGroup'
                    ? {
                          columns: VALUE_GROUP_DEFAULT_COLUMNS,
                          rows: VALUE_GROUP_DEFAULT_ROWS,
                          values: [],
                      }
                    : {},
        }
        const nextMeta = { ...metaRef.current, [id]: widget }
        metaRef.current = nextMeta
        setMeta(nextMeta)
        setLayouts((prev) => {
            const next: Layouts = { ...prev }
            for (const bp of BP_ORDER) {
                // Find the lowest free row so the new widget drops below existing ones.
                const existing = next[bp] ?? []
                const maxY = existing.reduce((m, l) => Math.max(m, l.y + l.h), 0)
                next[bp] = [
                    ...existing,
                    normalizeLayoutItem(
                        {
                            i: id,
                            x: 0,
                            y: maxY,
                            w: WIDGET_DEFS[type].fullWidth ? COLS[bp] : def.size.w,
                            h: def.size.h,
                        },
                        widget,
                        bp
                    ),
                ]
            }
            layoutsRef.current = next
            return next
        })
        setSelectedId(id)
        setDirty(true)
    }

    const removeWidget = (id: string) => {
        const nextMeta = { ...metaRef.current }
        delete nextMeta[id]
        metaRef.current = nextMeta
        setMeta(nextMeta)
        setLayouts((prev) => {
            const next: Layouts = {}
            for (const bp of BP_ORDER) next[bp] = (prev[bp] ?? []).filter((l) => l.i !== id)
            layoutsRef.current = next
            return next
        })
        if (selectedId === id) setSelectedId(null)
        setDirty(true)
    }

    const updateWidgetMeta = (id: string, patch: Partial<DashboardWidget>) => {
        const nextMeta = {
            ...metaRef.current,
            [id]: { ...metaRef.current[id], ...patch },
        }
        metaRef.current = nextMeta
        setMeta(nextMeta)
        setDirty(true)
    }

    const updateValueGroupSize = (
        widget: DashboardWidget,
        patch: Partial<{ columns: number; rows: number }>
    ) => {
        const columns = patch.columns ?? getValueGroupColumns(widget)
        const rows = patch.rows ?? getValueGroupRows(widget)
        const itemCount = columns * rows
        const values = serializeValueGroupItems(
            getValueGroupItems(widget).slice(0, itemCount)
        )

        updateWidgetMeta(widget.i, {
            options: {
                ...(widget.options ?? {}),
                columns,
                rows,
                values,
            },
        })
    }

    const updateValueGroupItem = (
        widget: DashboardWidget,
        index: number,
        attributeKey: string
    ) => {
        const columns = getValueGroupColumns(widget)
        const rows = getValueGroupRows(widget)
        const itemCount = columns * rows
        const values = getValueGroupItems(widget).slice(0, itemCount)

        while (values.length < itemCount) values.push({})

        if (attributeKey === VALUE_GROUP_NONE) {
            values[index] = {}
        } else {
            values[index] = {
                ...values[index],
                attributeKey,
            }
        }

        updateWidgetMeta(widget.i, {
            options: {
                ...(widget.options ?? {}),
                columns,
                rows,
                values,
            },
        })
    }

    const updateValueGroupItemTitle = (
        widget: DashboardWidget,
        index: number,
        label: string
    ) => {
        const columns = getValueGroupColumns(widget)
        const rows = getValueGroupRows(widget)
        const itemCount = columns * rows
        const values = getValueGroupItems(widget).slice(0, itemCount)

        while (values.length < itemCount) values.push({})

        values[index] = {
            ...values[index],
            label,
        }

        updateWidgetMeta(widget.i, {
            options: {
                ...(widget.options ?? {}),
                columns,
                rows,
                values,
            },
        })
    }

    const onLayoutChange = (_current: Layout[], all: Layouts) => {
        const normalized = normalizeLayouts(all, metaRef.current, layoutsRef.current)
        if (serializeLayouts(normalized) === serializeLayouts(layoutsRef.current)) return

        layoutsRef.current = normalized
        setLayouts(normalized)
        setDirty(true)
    }

    const handleSave = async () => {
        const currentLayouts = layoutsRef.current

        // Fold per-breakpoint positions back into each widget.
        const widgets: DashboardWidget[] = orderedWidgets.map((widget) => {
            const widgetLayouts: DashboardWidget['layouts'] = {}
            for (const bp of BP_ORDER) {
                const entry = (currentLayouts[bp] ?? []).find((l) => l.i === widget.i)
                if (entry) widgetLayouts[bp] = { x: entry.x, y: entry.y, w: entry.w, h: entry.h }
            }
            if (widget.type === 'valueGroup') {
                const columns = getValueGroupColumns(widget)
                const rows = getValueGroupRows(widget)
                const values = serializeValueGroupItems(
                    getValueGroupItems(widget).slice(0, columns * rows)
                )
                const options = { ...(widget.options ?? {}) }
                delete options.unit
                return {
                    ...widget,
                    title: titleOverride(widget.title) || null,
                    layouts: widgetLayouts,
                    options: {
                        ...options,
                        columns,
                        rows,
                        values,
                    },
                }
            }
            if (widget.type === 'metric') {
                const options = { ...(widget.options ?? {}) }
                delete options.unit
                return {
                    ...widget,
                    title: titleOverride(widget.title) || null,
                    layouts: widgetLayouts,
                    options,
                }
            }
            return { ...widget, layouts: widgetLayouts }
        })

        try {
            await updateDashboard.mutateAsync({ id: dashboardId!, data: { widgets } })
            toast.success('Dashboard saved')
            setDirty(false)
        } catch {
            toast.error('Failed to save dashboard')
        }
    }

    if (isLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8">
                Loading...
            </div>
        )
    }

    if (!dashboard) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8">
                Dashboard not found.
            </div>
        )
    }

    const selected = selectedId ? meta[selectedId] ?? null : null
    const activePreview = PREVIEW_MODES.find((mode) => mode.key === previewMode) ?? PREVIEW_MODES[0]
    const previewWidth = activePreview.width
    const editingLayoutLabel = breakpoint === 'lg' ? 'desktop / laptop' : 'tablet / phone'

    return (
        <div className="mx-auto py-6 lg:py-8 px-4 sm:px-6 lg:px-8">
            {/* Make RGL's resize handle clearly visible and not clipped. */}
            <style>{`
                .dash-canvas .react-grid-item > .react-resizable-handle {
                    width: 24px;
                    height: 24px;
                    right: 0;
                    bottom: 0;
                    z-index: 30;
                    opacity: 0.75;
                    transition: opacity 0.15s ease;
                }
                .dash-canvas .react-grid-item > .react-resizable-handle::after {
                    right: 6px;
                    bottom: 6px;
                    width: 8px;
                    height: 8px;
                    border-right-color: hsl(var(--primary));
                    border-bottom-color: hsl(var(--primary));
                }
                .dash-canvas .react-grid-item:hover > .react-resizable-handle {
                    opacity: 1;
                }
                .dash-canvas .react-grid-item {
                    cursor: grab;
                    user-select: none;
                }
                .dash-canvas .react-grid-item.react-draggable-dragging {
                    cursor: grabbing;
                }
                .dash-canvas .react-grid-item.react-grid-placeholder {
                    background: hsl(var(--primary));
                    opacity: 0.15;
                    border-radius: 0.5rem;
                }
                .dash-canvas .react-grid-item.react-draggable-dragging,
                .dash-canvas .react-grid-item.resizing {
                    z-index: 20;
                }
            `}</style>

            <div className="mb-4">
                <Link
                    to={`/models/${modelId}/dashboards`}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboards
                </Link>
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-semibold text-foreground">
                            {dashboard.name}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {model?.name} · drag a card to move, drag the
                            bottom-right corner to resize.{' '}
                            <span className="text-foreground/70">
                                Editing{' '}
                                <span className="font-medium uppercase">
                                    {editingLayoutLabel}
                                </span>{' '}
                                layout
                            </span>
                        </p>
                    </div>
                    <Button onClick={handleSave} disabled={!dirty || updateDashboard.isPending}>
                        <Save className="h-4 w-4 mr-1.5" />
                        {dirty ? 'Save changes' : 'Saved'}
                    </Button>
                </div>
            </div>

            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground mr-1">
                        Add widget:
                    </span>
                    {(Object.keys(WIDGET_DEFS) as WidgetType[]).map((type) => {
                        const def = WIDGET_DEFS[type]
                        const Icon = def.icon
                        return (
                            <Button
                                key={type}
                                variant="outline"
                                size="sm"
                                onClick={() => addWidget(type)}
                            >
                                <Icon className="h-4 w-4 mr-1.5" />
                                {def.label}
                            </Button>
                        )
                    })}
                </div>

                <div className="flex flex-wrap items-center gap-1 rounded-md border bg-background p-1">
                    {PREVIEW_MODES.map((mode) => {
                        const Icon = mode.icon
                        const active = previewMode === mode.key
                        return (
                            <Button
                                key={mode.key}
                                type="button"
                                variant={active ? 'secondary' : 'ghost'}
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => handlePreviewModeChange(mode.key)}
                            >
                                <Icon className="h-4 w-4 mr-1.5" />
                                {mode.label}
                            </Button>
                        )
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-4 xl:flex-row">
                {/* Canvas */}
                <div className="flex-1 min-w-0 overflow-x-auto rounded-lg border bg-muted/30">
                    <div
                        ref={setCanvasRef}
                        className={`${previewWidth ? 'mx-auto' : ''} min-h-64`}
                        style={{
                            width: previewWidth ? `${previewWidth}px` : '100%',
                            maxWidth: previewWidth ? 'none' : '100%',
                        }}
                    >
                        {orderedWidgets.length === 0 ? (
                            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
                                Add a widget to start designing this dashboard.
                            </div>
                        ) : gridWidth === 0 ? null : (
                            <Responsive
                                className="dash-canvas"
                                width={gridWidth}
                                layouts={layouts}
                                breakpoints={BREAKPOINTS}
                                cols={COLS}
                                rowHeight={ROW_HEIGHT}
                                margin={[12, 12]}
                                containerPadding={[8, 8]}
                                isDraggable
                                isResizable
                                isBounded
                                draggableCancel=".widget-no-drag, button, input, textarea, [role='combobox'], [role='option']"
                                breakpoint={activePreview.layout ?? undefined}
                                resizeHandles={['se']}
                                compactType="vertical"
                                onLayoutChange={onLayoutChange}
                                onBreakpointChange={(bp) =>
                                    setBreakpoint(bp as Breakpoint)
                                }
                            >
                                {orderedWidgets.map((widget) => {
                                    const def = WIDGET_DEFS[widget.type]
                                    const Icon = iconForWidget(widget)
                                    const isSelected = widget.i === selectedId
                                    return (
                                        <div
                                            key={widget.i}
                                            data-widget-type={widget.type}
                                            data-widget-icon={iconOptionForWidget(widget).value}
                                            data-widget-columns={widget.type === 'valueGroup' ? getValueGroupColumns(widget) : undefined}
                                            data-widget-rows={widget.type === 'valueGroup' ? getValueGroupRows(widget) : undefined}
                                            className={`group relative select-none rounded-lg ${
                                                isSelected
                                                    ? 'ring-2 ring-primary ring-offset-2'
                                                    : 'hover:ring-1 hover:ring-primary/40'
                                            }`}
                                            onMouseDownCapture={(event) => {
                                                if (!isWidgetControlTarget(event.target)) {
                                                    selectWidget(widget.i)
                                                }
                                            }}
                                        >
                                            <div className={`absolute right-2 top-2 z-20 flex items-center gap-1 rounded-md border bg-background/95 px-1.5 py-1 shadow-sm transition-opacity ${
                                                isSelected
                                                    ? 'opacity-100'
                                                    : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                                            }`}>
                                                <GripVertical className="h-3.5 w-3.5 text-muted-foreground/70" />
                                                {Icon && (
                                                    <Icon className="h-3.5 w-3.5 text-muted-foreground/70" />
                                                )}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove ${widget.title || def.label}`}
                                                    className="widget-no-drag shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        removeWidget(widget.i)
                                                    }}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                            <div
                                                className="h-full w-full cursor-grab active:cursor-grabbing"
                                                onClick={() => selectWidget(widget.i)}
                                            >
                                                <WidgetPreview
                                                    widget={widget}
                                                    label={attributeLabel(widget.attributeKey)}
                                                    attributeLabelForKey={attributeLabel}
                                                    attributeUnitForKey={attributeUnit}
                                                />
                                            </div>
                                        </div>
                                    )
                                })}
                            </Responsive>
                        )}
                    </div>
                </div>

                {/* Inspector */}
                <div className="w-full shrink-0 xl:w-72">
                    <div className="rounded-lg border bg-card p-4 sticky top-6">
                        {!selected ? (
                            <p className="text-sm text-muted-foreground">
                                Select a widget to configure it.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-foreground">
                                        {WIDGET_DEFS[selected.type].label}
                                    </h3>
                                    <button
                                        type="button"
                                        aria-label="Close inspector"
                                        className="text-muted-foreground hover:text-foreground"
                                        onClick={() => setSelectedId(null)}
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="widget-title">
                                        {titleLabelForWidget(selected.type)}
                                    </Label>
                                    <Input
                                        id="widget-title"
                                        value={selected.title ?? ''}
                                        placeholder={titlePlaceholderForWidget(
                                            selected.type
                                        )}
                                        onChange={(e) =>
                                            updateWidgetMeta(selected.i, {
                                                title: e.target.value,
                                            })
                                        }
                                    />
                                </div>

                                {selected.type !== 'header' && (
                                    <div className="space-y-1.5">
                                        <Label htmlFor="widget-icon">Icon</Label>
                                        <Select
                                            value={
                                                iconOptionForWidget(selected)
                                                    .value
                                            }
                                            onValueChange={(value) => {
                                                const options = {
                                                    ...(selected.options ?? {}),
                                                }
                                                if (value === 'default') {
                                                    delete options.icon
                                                } else {
                                                    options.icon = value
                                                }
                                                updateWidgetMeta(selected.i, {
                                                    options,
                                                })
                                            }}
                                        >
                                            <SelectTrigger id="widget-icon">
                                                <SelectValue>
                                                    {iconLabelForWidget(
                                                        selected
                                                    )}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ICON_OPTIONS.map((option) => {
                                                    const Icon =
                                                        option.value === 'none'
                                                            ? null
                                                            : option.icon ??
                                                              WIDGET_DEFS[
                                                                  selected.type
                                                              ].icon
                                                    return (
                                                        <SelectItem
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            <span className="flex items-center gap-2">
                                                                {Icon ? (
                                                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                                                ) : (
                                                                    <span className="h-4 w-4" />
                                                                )}
                                                                <span>
                                                                    {option.value ===
                                                                    'default'
                                                                        ? 'Default'
                                                                        : option.label}
                                                                </span>
                                                            </span>
                                                        </SelectItem>
                                                    )
                                                })}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {selected.type === 'valueGroup' &&
                                    (() => {
                                        const columns =
                                            getValueGroupColumns(selected)
                                        const rows = getValueGroupRows(selected)
                                        const itemCount = columns * rows
                                        const items =
                                            getValueGroupItems(selected)

                                        return (
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="space-y-1.5">
                                                        <Label htmlFor="widget-group-columns">
                                                            Values wide
                                                        </Label>
                                                        <Select
                                                            value={String(
                                                                columns
                                                            )}
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                updateValueGroupSize(
                                                                    selected,
                                                                    {
                                                                        columns:
                                                                            Number(
                                                                                value
                                                                            ),
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger id="widget-group-columns">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {Array.from(
                                                                    {
                                                                        length:
                                                                            VALUE_GROUP_MAX_COLUMNS,
                                                                    },
                                                                    (_, index) =>
                                                                        index +
                                                                        1
                                                                ).map(
                                                                    (
                                                                        value
                                                                    ) => (
                                                                        <SelectItem
                                                                            key={
                                                                                value
                                                                            }
                                                                            value={String(
                                                                                value
                                                                            )}
                                                                        >
                                                                            {
                                                                                value
                                                                            }
                                                                        </SelectItem>
                                                                    )
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    <div className="space-y-1.5">
                                                        <Label htmlFor="widget-group-rows">
                                                            Values high
                                                        </Label>
                                                        <Select
                                                            value={String(
                                                                rows
                                                            )}
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                updateValueGroupSize(
                                                                    selected,
                                                                    {
                                                                        rows: Number(
                                                                            value
                                                                        ),
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger id="widget-group-rows">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {Array.from(
                                                                    {
                                                                        length:
                                                                            VALUE_GROUP_MAX_ROWS,
                                                                    },
                                                                    (_, index) =>
                                                                        index +
                                                                        1
                                                                ).map(
                                                                    (
                                                                        value
                                                                    ) => (
                                                                        <SelectItem
                                                                            key={
                                                                                value
                                                                            }
                                                                            value={String(
                                                                                value
                                                                            )}
                                                                        >
                                                                            {
                                                                                value
                                                                            }
                                                                        </SelectItem>
                                                                    )
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label>Values</Label>
                                                    <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                                                        {Array.from({
                                                            length: itemCount,
                                                        }).map((_, index) => {
                                                            const item =
                                                                items[index]
                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className="space-y-1"
                                                                >
                                                                    <p className="text-xs font-medium text-muted-foreground">
                                                                        Value{' '}
                                                                        {index +
                                                                            1}
                                                                    </p>
                                                                    <AttributeCombobox
                                                                        id={`widget-group-value-${index}`}
                                                                        attributes={
                                                                            attributes
                                                                        }
                                                                        value={
                                                                            item?.attributeKey ??
                                                                            null
                                                                        }
                                                                        emptyValue={
                                                                            VALUE_GROUP_NONE
                                                                        }
                                                                        emptyLabel="Empty"
                                                                        onChange={(
                                                                            value
                                                                        ) =>
                                                                            updateValueGroupItem(
                                                                                selected,
                                                                                index,
                                                                                value
                                                                            )
                                                                        }
                                                                    />
                                                                    <div className="space-y-1">
                                                                        <Label
                                                                            htmlFor={`widget-group-title-${index}`}
                                                                            className="text-xs font-medium text-muted-foreground"
                                                                        >
                                                                            Title
                                                                            override
                                                                        </Label>
                                                                        <Input
                                                                            id={`widget-group-title-${index}`}
                                                                            value={
                                                                                item?.label ??
                                                                                ''
                                                                            }
                                                                            placeholder="Use attribute friendly name"
                                                                            disabled={
                                                                                !item?.attributeKey
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                updateValueGroupItemTitle(
                                                                                    selected,
                                                                                    index,
                                                                                    e
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                    {attributes.length ===
                                                        0 && (
                                                        <p className="text-xs text-muted-foreground">
                                                            This model has no
                                                            attributes yet.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })()}

                                {selected.type === 'header' && (
                                    <div className="space-y-1.5">
                                        <Label htmlFor="widget-subtitle">
                                            Subtitle
                                        </Label>
                                        <Input
                                            id="widget-subtitle"
                                            value={
                                                (selected.options
                                                    ?.subtitle as string) ?? ''
                                            }
                                            placeholder="Optional section description"
                                            onChange={(e) =>
                                                updateWidgetMeta(selected.i, {
                                                    options: {
                                                        ...(selected.options ??
                                                            {}),
                                                        subtitle:
                                                            e.target.value,
                                                    },
                                                })
                                            }
                                        />
                                    </div>
                                )}

                                {WIDGET_DEFS[selected.type].bindable && (
                                    <>
                                        <div className="space-y-1.5">
                                            <Label>Attribute</Label>
                                            <AttributeCombobox
                                                attributes={attributes}
                                                value={
                                                    selected.attributeKey ??
                                                    null
                                                }
                                                onChange={(value) => {
                                                    const options = {
                                                        ...(selected.options ??
                                                            {}),
                                                    }
                                                    delete options.unit
                                                    updateWidgetMeta(
                                                        selected.i,
                                                        {
                                                            attributeKey: value,
                                                            options,
                                                        }
                                                    )
                                                }}
                                            />
                                            {attributes.length === 0 && (
                                                <p className="text-xs text-muted-foreground">
                                                    This model has no attributes
                                                    yet.
                                                </p>
                                            )}
                                        </div>

                                    </>
                                )}

                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full text-destructive hover:text-destructive"
                                    onClick={() => removeWidget(selected.i)}
                                >
                                    <Trash2 className="h-4 w-4 mr-1.5" />
                                    Remove widget
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
